import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { EMPTY, Observable, combineLatest, defer, of, switchMap, throwError, timer } from 'rxjs';
import { catchError, filter, last, takeUntil, tap } from 'rxjs/operators';
import {
  Action,
  Actions,
  createSelector,
  NgxsOnInit,
  ofActionDispatched,
  State,
  StateContext,
} from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { ReduceMap } from '@libs/reduce-map';
import { RxQueue } from '@libs/rx-queue';
import { PaginationState } from '@apps/clos-sessions/shared/pagination/types';
import { ArchivesAssetsApi } from './archives-assets.api';
import { AssetsLocalStorage, Binary } from './assets-local-storage';
import { withFileNameSuffix } from './file-name-suffix';
import {
  CancelAssetUpload,
  FetchArchiveBucket,
  FetchArchiveBucketNextPaginationChunk,
  RateArchiveAsset,
  RemoveArchiveAssets,
  UploadAssets,
} from './archives-assets.actions';
import {
  ClosArchiveBucket,
  ClosAsset,
  ClosAssetUploading,
  ClosBucketState,
  ClosUploadStatus,
} from './types';
import { DeepPartial } from '@libs/utility-types';
import {
  StorjUploadCancelledError,
  StorjUploadService,
} from '@apps/clos-sessions/services/storj-upload';
import { SignOut } from '@apps/clos-sessions/state/auth';

export type AssetsCollection = Record<ClosAsset['id'], ClosAsset>;

export type BucketsCollection = Record<ClosBucketState['id'], ClosBucketState>;

export type UploadingCollection = Record<ClosAssetUploading['id'], ClosAssetUploading>;

export interface ArchiveAssetsStateModel {
  uploading: UploadingCollection;
  bucketsCollection: BucketsCollection;
  assetsCollection: AssetsCollection;
}

/** Where a binary is uploaded: the registered asset and the name it is registered under. */
type UploadSlot = Pick<ClosAsset, 'id' | 'name' | 'bucket' | 'archiveId'>;

interface UploadBinaryInput {
  binaryDetails: Binary;
  assetSlot: UploadSlot;
}

/** Copy names tried for a file whose name the archive already holds. */
const MAX_FILE_NAME_SUFFIX = 20;

/** A saved multipart plan only resumes an upload under the name it was started under. */
const resumableMultipartPlan = (
  { multipartUpload, multipartUploadName, name }: Binary,
  uploadName: string,
) => ((multipartUploadName ?? name) === uploadName ? multipartUpload : undefined);

const buildBucketId = ({ bucket, archiveId }: { archiveId: string; bucket: ClosArchiveBucket }) => {
  return `${archiveId}-${bucket}`;
};

@State<ArchiveAssetsStateModel>({
  name: 'archivesAssets',
  defaults: {
    uploading: {},
    bucketsCollection: {},
    assetsCollection: {},
  },
})
@Injectable()
export class ArchiveAssetsState implements NgxsOnInit {
  static assetsCollection = createSelector(
    [ArchiveAssetsState],
    ({ assetsCollection }: ArchiveAssetsStateModel) => assetsCollection,
  );

  static bucketsCollection = createSelector(
    [ArchiveAssetsState],
    ({ bucketsCollection }: ArchiveAssetsStateModel) => bucketsCollection,
  );

  static uploading = createSelector(
    [ArchiveAssetsState],
    ({ uploading }: ArchiveAssetsStateModel) => uploading,
  );

  static hasArchiveBucketsRegistered = ({ archiveId }: { archiveId: string }) => {
    return createSelector(
      [ArchiveAssetsState.bucketsCollection],
      (bucketsCollection: BucketsCollection) => {
        const keys = [
          buildBucketId({ bucket: ClosArchiveBucket.session, archiveId }),
          buildBucketId({ bucket: ClosArchiveBucket.sharing, archiveId }),
        ];

        return keys.every((key) => bucketsCollection[key]);
      },
    );
  };

  static archiveAssets = ({ archiveId }: { archiveId: string }) =>
    createSelector([ArchiveAssetsState.assetsCollection], (collection: AssetsCollection) => {
      return Object.values(collection).filter(({ archiveId: id }) => archiveId === id);
    });

  static archiveUploading = ({ archiveId }: { archiveId: string }) => {
    return createSelector(
      [ArchiveAssetsState.uploading],
      (uploadingCollection: UploadingCollection): ClosAssetUploading[] => {
        return Object.values(uploadingCollection).filter(({ archiveId: id }) => archiveId === id);
      },
    );
  };

  protected actions$ = inject(Actions);

  protected archiveAssetsApi = inject(ArchivesAssetsApi);

  protected assetsLocalStorage = inject(AssetsLocalStorage);

  protected storjUpload = inject(StorjUploadService);

  protected queue$$ = new RxQueue({ parallel: 5 });

  protected ctx?: StateContext<ArchiveAssetsStateModel>;

  ngxsOnInit(ctx: StateContext<ArchiveAssetsStateModel>) {
    this.ctx = ctx;
  }

  /**
   * Account-scoped data must not survive a logout (the next account can sign in without a reload).
   * `uploading` is kept: in-flight uploads keep patching their own entries until they settle.
   */
  @Action(SignOut)
  protected onSignOut({ patchState }: StateContext<ArchiveAssetsStateModel>): void {
    patchState({ bucketsCollection: {}, assetsCollection: {} });
  }

  @Action(FetchArchiveBucket)
  protected onFetchArchiveBucket(
    { setState }: StateContext<ArchiveAssetsStateModel>,
    { payload }: FetchArchiveBucket,
  ) {
    // techdebt https://github.com/CLOS-Inc/CLOS_Frontend/issues/570
    return this.archiveAssetsApi
      .getAssets({
        archive: { id: payload.archiveId },
        bucket: payload.bucket,
        take: 100,
        skip: 0,
      })
      .pipe(
        tap(({ hasNextChunk, list: assets }) => {
          const bucketId = buildBucketId({ bucket: payload.bucket, archiveId: payload.archiveId });

          setState(
            patch({
              bucketsCollection: patch({
                [bucketId]: {
                  id: bucketId,
                  type: payload.bucket,
                  state: hasNextChunk ? PaginationState.hasMore : PaginationState.complete,
                },
              }),
              assetsCollection: patch(new ReduceMap(assets).resolveBy('id')),
            }),
          );
        }),
      );
  }

  @Action(FetchArchiveBucketNextPaginationChunk)
  protected onFetchArchiveBucketNextPaginationChunk(
    { getState, setState }: StateContext<ArchiveAssetsStateModel>,
    { payload }: FetchArchiveBucketNextPaginationChunk,
  ) {
    const { assetsCollection } = getState();

    const bucketAssets = Object.values(assetsCollection).filter(({ archiveId, bucket }) => {
      return archiveId === payload.archiveId && bucket === payload.bucket;
    });

    return this.archiveAssetsApi
      .getAssets({
        archive: { id: payload.archiveId },
        bucket: payload.bucket,
        take: 20,
        skip: bucketAssets.length,
      })
      .pipe(
        tap(({ hasNextChunk, list }) => {
          const state = hasNextChunk ? PaginationState.hasMore : PaginationState.complete;

          setState(
            patch({
              assetsCollection: patch(new ReduceMap(list).resolveBy('id')),
              bucketsCollection: patch({
                [buildBucketId(payload)]: patch({ state }),
              }),
            }),
          );
        }),
      );
  }

  @Action(RemoveArchiveAssets)
  protected onRemoveArchiveAssets(
    { patchState, getState }: StateContext<ArchiveAssetsStateModel>,
    { payload }: RemoveArchiveAssets,
  ) {
    return this.archiveAssetsApi.removeAssets(payload).pipe(
      tap(() => {
        const { assetsCollection: initial } = getState();
        const removeMap = new ReduceMap(payload.assets).resolveBy('id');
        const assetsCollection = new ReduceMap(
          Object.entries(initial).filter(([assetId]) => !removeMap[assetId]),
        ).resolveBy(([_, asset]) => ({ [asset.id]: asset }));

        patchState({ assetsCollection });
      }),
    );
  }

  @Action(RateArchiveAsset)
  protected onRateArchiveAsset(
    { getState }: StateContext<ArchiveAssetsStateModel>,
    { payload }: RateArchiveAsset,
  ) {
    const initial = getState().assetsCollection[payload.asset.id];
    const cancel$ = this.actions$.pipe(
      ofActionDispatched(RateArchiveAsset),
      filter((action: RateArchiveAsset) => {
        return (
          action.payload.archive.id === payload.archive.id &&
          action.payload.asset.id === payload.asset.id
        );
      }),
    );

    this.patchAssetRate(payload.asset.id, payload.score);

    return timer(500).pipe(
      switchMap(() => this.archiveAssetsApi.rateAsset(payload)),
      catchError((err) => {
        this.patchAssetRate(payload.asset.id, initial?.rate);

        return throwError(() => err);
      }),
      takeUntil(cancel$),
    );
  }

  @Action(UploadAssets)
  protected onUploadAsset(
    { setState }: StateContext<ArchiveAssetsStateModel>,
    { payload }: UploadAssets,
  ) {
    return this.assetsLocalStorage.getMany(payload.guids).pipe(
      tap((binaries) => {
        if (binaries.length !== payload.guids.length) {
          throw new Error(`Some binary haven't been registered in the local cache is not found`);
        }

        setState(
          patch({
            uploading: patch(
              binaries.reduce<UploadingCollection>(
                (acc, binary) => ({
                  ...acc,
                  [binary.id]: {
                    id: binary.id,
                    status: ClosUploadStatus.awaiting,
                    archiveId: payload.archive.id,
                    bucket: payload.bucket,
                    name: binary.name,
                    progress: {
                      bytesUploaded: 0,
                      totalBytes: binary.size,
                      percent: 0,
                    },
                  },
                }),
                {},
              ),
            ),
          }),
        );
      }),
      switchMap((assetBinaryDetails) =>
        this.archiveAssetsApi
          .registerAssets({
            ...payload,
            assetBinaryDetails,
          })
          .pipe(
            catchError((error) => {
              assetBinaryDetails.forEach(({ id }) => {
                this.patchUploading({ id, status: ClosUploadStatus.error });
              });
              return throwError(() => error);
            }),
            switchMap((registerDict) => {
              return combineLatest(
                assetBinaryDetails.map((binaryDetails) =>
                  this.uploadAssetBinary({
                    binaryDetails,
                    assetSlot: {
                      archiveId: payload.archive.id,
                      bucket: payload.bucket,
                      id: registerDict[binaryDetails.id],
                      name: binaryDetails.name,
                    },
                  }),
                ),
              );
            }),
          ),
      ),
    );
  }

  protected uploadAssetBinary(input: UploadBinaryInput): Observable<unknown> {
    const {
      binaryDetails,
      assetSlot: { archiveId, bucket, id: assetId, name },
    } = input;
    const archive = { id: archiveId };
    const cancel$ = this.actions$.pipe(
      ofActionDispatched(CancelAssetUpload),
      filter((action: CancelAssetUpload) => action.payload.id === binaryDetails.id),
    );

    if (!assetId) {
      // The API returns an empty ID for a name the archive already holds.
      return this.registerUnderSuffix(input, 1).pipe(
        switchMap((assetSlot) => {
          this.patchUploading({ id: binaryDetails.id, name: assetSlot.name });
          return this.uploadAssetBinary({ binaryDetails, assetSlot });
        }),
        catchError((error) => {
          this.patchUploading({ id: binaryDetails.id, status: ClosUploadStatus.error });
          return throwError(() => error);
        }),
      );
    }

    return this.queue$$.push({
      id: binaryDetails.id,
      // eslint-disable-next-line rxjs/finnish
      emitter: defer(() =>
        this.storjUpload
          .upload({
            file: binaryDetails.binary,
            imageName: name,
            roomId: archive.id,
            type: bucket === ClosArchiveBucket.sharing ? 'retouch' : 'original',
            multipartPlan: resumableMultipartPlan(binaryDetails, name),
            persistMultipartPlan: (plan) =>
              this.assetsLocalStorage.updateMultipartUpload(binaryDetails, plan, name),
            cancel$,
          })
          .pipe(
            tap((event) => {
              if (event.type === 'start') {
                return this.patchUploading({
                  id: binaryDetails.id,
                  status: ClosUploadStatus.inProgress,
                });
              }

              this.patchUploading({
                id: binaryDetails.id,
                progress:
                  event.type === 'uploading'
                    ? event.progress
                    : { percent: 100, bytesUploaded: binaryDetails.size },
                status:
                  event.type === 'complete'
                    ? ClosUploadStatus.success
                    : ClosUploadStatus.inProgress,
              });
            }),
            last(),
            switchMap(() => {
              return this.archiveAssetsApi.getAssetById({ archive, assetId }).pipe(
                catchError((error: unknown) => {
                  // Some API versions return a reservation ID that is no longer
                  // present after completion. The object is addressed by name.
                  if (
                    (error instanceof HttpErrorResponse && error.status === 404) ||
                    (typeof error === 'object' &&
                      error !== null &&
                      'code' in error &&
                      (error as { code: unknown }).code === 401)
                  ) {
                    return this.archiveAssetsApi.findAssetByName(archive, bucket, name);
                  }
                  return throwError(() => error);
                }),
                switchMap((asset) =>
                  // The local binary is only kept to resume the upload; drop it once confirmed.
                  this.assetsLocalStorage
                    .remove(binaryDetails.id)
                    .pipe(tap(() => this.swapUploadingWithAsset(binaryDetails.id, asset))),
                ),
              );
            }),
            catchError((error) => {
              if (error instanceof StorjUploadCancelledError) {
                this.removeUploading(binaryDetails.id);
                return this.assetsLocalStorage
                  .remove(binaryDetails.id)
                  .pipe(switchMap(() => EMPTY));
              }

              this.patchUploading({ id: binaryDetails.id, status: ClosUploadStatus.error });
              return throwError(() => error);
            }),
          ),
      ),
    });
  }

  /**
   * A taken name can belong to a completed photo, even in the target bucket. Only upload after
   * registration grants a new ID; a filename lookup cannot prove that an asset is ours to resume.
   */
  protected registerUnderSuffix(input: UploadBinaryInput, suffix: number): Observable<UploadSlot> {
    const { binaryDetails, assetSlot } = input;
    if (suffix > MAX_FILE_NAME_SUFFIX) {
      return throwError(() => new Error(`No free copy name for ${binaryDetails.name}`));
    }
    const name = withFileNameSuffix(binaryDetails.name, suffix);

    return this.archiveAssetsApi
      .registerAssets({
        archive: { id: assetSlot.archiveId },
        bucket: assetSlot.bucket,
        assetBinaryDetails: [{ ...binaryDetails, name }],
      })
      .pipe(
        switchMap(({ [binaryDetails.id]: id }) =>
          id ? of({ ...assetSlot, id, name }) : this.registerUnderSuffix(input, suffix + 1),
        ),
      );
  }

  protected swapUploadingWithAsset(uploadingId: ClosAssetUploading['id'], asset: ClosAsset): void {
    const {
      uploading: { [uploadingId]: _, ...uploading },
      assetsCollection,
    } = this.ctx.getState();

    this.ctx.patchState({
      uploading,
      assetsCollection: {
        ...assetsCollection,
        [asset.id]: { ...asset, guid: uploadingId },
      },
    });
  }

  protected patchAssetRate(assetId: string, rate: number) {
    return this.patchAsset({
      id: assetId,
      rate,
      dto: {
        score: rate,
      },
    });
  }

  protected patchAsset(input: DeepPartial<ClosAsset>): void {
    this.ctx.setState(
      patch({
        assetsCollection: patch({
          [input.id]: patch({
            ...input,
            dto: patch({ ...(input.dto || {}) }),
          }),
        }),
      }),
    );
  }

  protected patchUploading(input: DeepPartial<ClosAssetUploading>): void {
    this.ctx.setState(
      patch({
        uploading: patch({
          [input.id as string]: patch({
            ...input,
            progress: patch({
              ...input.progress,
            }),
          }),
        }),
      }),
    );
  }

  protected removeUploading(uploadingId: ClosAssetUploading['id']): void {
    const {
      uploading: { [uploadingId]: _, ...uploading },
    } = this.ctx.getState();

    this.ctx.patchState({ uploading });
  }
}
